import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { toast } from "sonner";
import { useSendEmailOtp } from "@/features/auth/services";
import {
  getPendingEmailVerification,
  setPendingEmailVerification,
  type EmailVerificationSource,
} from "../utils/email-verification";
import { buildEmailVerificationCodePath } from "../utils/email-verification-flow";
import Button from "../../../shared/components/Button";
import Input from "../../../shared/components/Input";
import LinearLayout from "../../../shared/components/LinearLayout";
import SecondaryIcon from "../../../shared/components/SecondaryIcon";
import Icons from "../../../shared/constants/icon.constant";

const emailSchema = z
  .string()
  .trim()
  .min(1, "Email is required")
  .email("Enter a valid email address");

const getRedirectParam = (value?: string | null) =>
  value && value.length > 0 ? value : null;

export default function EmailVerifyForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const locationState =
    (location.state as {
      email?: string;
      redirectTo?: string;
      redirectPath?: string;
      otpSentAt?: number;
      source?: EmailVerificationSource;
    } | null) ?? null;
  const rememberedVerification = getPendingEmailVerification();
  const redirectParamFromState =
    locationState?.redirectPath ?? locationState?.redirectTo ?? null;
  const redirectParamFromQuery = getRedirectParam(
    searchParams.get("redirectTo")
  );
  const redirectPath =
    redirectParamFromState ??
    redirectParamFromQuery ??
    rememberedVerification?.redirectPath ??
    null;
  const source = locationState?.source ?? rememberedVerification?.source;
  const initialEmail =
    locationState?.email ??
    searchParams.get("email") ??
    rememberedVerification?.email ??
    "";
  const otpSentAt =
    locationState?.otpSentAt ?? rememberedVerification?.otpSentAt;

  const [email, setEmail] = useState(initialEmail);
  const [error, setError] = useState<string | null>(null);
  const { mutateAsync: sendEmailOtp, isPending } = useSendEmailOtp();

  useEffect(() => {
    setEmail(initialEmail);
  }, [initialEmail]);

  useEffect(() => {
    const normalizedEmail = initialEmail.trim();

    if (!normalizedEmail) {
      return;
    }

    setPendingEmailVerification(normalizedEmail, {
      redirectPath: redirectPath ?? undefined,
      otpSentAt,
      source,
    });

    navigate(
      buildEmailVerificationCodePath({
        email: normalizedEmail,
        redirectPath,
      }),
      {
        replace: true,
        state: {
          email: normalizedEmail,
          redirectPath,
          otpSentAt,
          source,
        },
      }
    );
  }, [initialEmail, navigate, otpSentAt, redirectPath, source]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    const result = emailSchema.safeParse(email);

    if (!result.success) {
      setError(result.error.issues[0]?.message ?? "Enter a valid email");
      return;
    }

    const normalizedEmail = result.data;

    try {
      await sendEmailOtp({ email: normalizedEmail });
      const now = Date.now();
      setPendingEmailVerification(normalizedEmail, {
        redirectPath: redirectPath ?? undefined,
        otpSentAt: now,
        source,
      });
      toast.success("Verification code has been sent to your email.");

      navigate(
        buildEmailVerificationCodePath({
          email: normalizedEmail,
          redirectPath,
        }),
        {
          state: {
            email: normalizedEmail,
            redirectPath,
            otpSentAt: now,
            source,
          },
          replace: true,
        }
      );
    } catch (submissionError) {
      const message =
        submissionError instanceof Error
          ? submissionError.message
          : "Unable to send verification code. Please try again.";
      toast.error(message);
      setError(message);
    }
  };

  return (
    <div className="w-full">
      <LinearLayout orientation="vertical">
        <form
          onSubmit={handleSubmit}
          className="text-center align-middle mx-auto space-y-5">
          <h1 className="text-white text-[20px] leading-[24px] font-medium ">
            Verify Your Email
          </h1>
          <p className="text-white text-sm font-sm opacity-50">
            We will send a verification code to your email address
          </p>
          <div>
            <Input
              leftElement={
                <SecondaryIcon
                  icon={Icons.EmailIcon({ width: 17, height: 17 })}
                  isActive
                />
              }
              type="email"
              placeholder="name@email.com"
              fluid
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                if (error) {
                  setError(null);
                }
              }}
            />
            {error && (
              <p className="text-danger text-sm text-left mt-1">{error}</p>
            )}
          </div>
          <Button type="submit" variant="primary" fluid loading={isPending}>
            {isPending ? "Sending..." : "Send Code"}
          </Button>
        </form>
      </LinearLayout>
    </div>
  );
}
